import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Validators } from '@angular/forms';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { TypedFormBuilder, TypedFormControl, TypedFormGroup } from '@gmrappdev/formsafe';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { CurrentUserService } from 'src/app/core/current-user.service';
import { AirCarrierHttpClient } from 'src/app/shared/flight-log-api/air-carriers/air-carriers-client.service';
import {
  EmployeeHttpClient,
  EmployeePrdPermissionsParams,
} from 'src/app/shared/flight-log-api/employees/employee-client.service';
import {
  CommandError,
  CommandResult,
  CommandResultStatus,
  UserRole,
} from 'src/app/shared/flight-log-api/shared/shared-client.model';
import { SelectItem } from 'src/util/ui/forms';

@Component({
  selector: 'fl-prd-permissions-page',
  templateUrl: './prd-permissions-page.component.html',
  styleUrls: ['./prd-permissions-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrdPermissionsPageComponent implements OnInit, OnDestroy {
  public form: TypedFormGroup<EmployeePrdPermissionsParams>;
  public roleOptions: SelectItem[];
  public airCarrierOptions$: Observable<SelectItem[]>;
  public employeeCtrl = new TypedFormControl<string>();
  public airCarriersCtrl = new TypedFormControl<string[]>([]);
  public rolesCtrl = new TypedFormControl<string[]>([]);
  private _onDestroy$ = new Subject<void>();
  public errors$: Subject<CommandError[]> = new Subject<CommandError[]>();
  public clicked = false;

  constructor(
    private _fb: TypedFormBuilder,
    private _user: CurrentUserService,
    private _airCarriers: AirCarrierHttpClient,
    private _employees: EmployeeHttpClient,
    private _snackBar: MatSnackBar,
  ) {}

  public ngOnInit() {
    this.form = this._fb.group<EmployeePrdPermissionsParams>({
      id: [null, Validators.required],
      airCarrierIds: this._fb.array<string>([]),
      userRoles: this._fb.array<string>([]),
    });

    const allAirCarriers$ = this._airCarriers
      .getAll()
      .pipe(map((airCarriers) => airCarriers.results));

    this.airCarrierOptions$ = combineLatest([this._user.valueChanges, allAirCarriers$]).pipe(
      map(([user, allAirCarriers]) => {
        return allAirCarriers.map((airCarrier) => {
          const disabled =
            user?.accessibleAirCarriers.find((ac) => ac.id === airCarrier.id) == null;
          return { label: airCarrier?.name, value: airCarrier?.id, disabled } as SelectItem;
        });
      }),
    );

    this.roleOptions = [
      { value: UserRole.PrdUser, label: 'PRD User' },
      { value: UserRole.PrdAdmin, label: 'PRD Admin' },
    ];

    this.airCarriersCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((airCarriersCtrlvalues) => {
        this.form.controls.airCarrierIds.reset();
        if (airCarriersCtrlvalues?.length != null && airCarriersCtrlvalues?.length > 0) {
          airCarriersCtrlvalues.forEach((airCarrier) =>
            this.form.controls.airCarrierIds.value.push(airCarrier),
          );
        }
        this.form.updateValueAndValidity();
      });

    this.rolesCtrl.valueChanges.pipe(takeUntil(this._onDestroy$)).subscribe((rolesCtrlvalues) => {
      this.form.controls.userRoles.reset();
      if (rolesCtrlvalues?.length != null && rolesCtrlvalues?.length > 0) {
        rolesCtrlvalues.forEach((role) => this.form.controls.userRoles.value.push(role));
      }
      this.form.updateValueAndValidity();
    });

    // Populate form values when employee is selected
    this.employeeCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(async (employeeId) => {
        this.form.controls.id.setValue(employeeId);
        if (employeeId != null) {
          const employee = await this._employees.getById(employeeId).toPromise();

          this.form.controls.airCarrierIds.reset();
          const airCarriersArray: string[] = [];
          if (
            employee?.accessibleAirCarriers != null &&
            employee.accessibleAirCarriers.length > 0
          ) {
            employee.accessibleAirCarriers.forEach((airCarrier) => {
              airCarriersArray.push(airCarrier.id);
            });
          }
          this.airCarriersCtrl.setValue(airCarriersArray);
          this.airCarriersCtrl.updateValueAndValidity();

          this.form.controls.userRoles.reset();
          const rolesArray: string[] = [];
          if (employee?.userRoles != null && employee.userRoles.length > 0) {
            employee.userRoles.forEach((role) => {
              if (role === UserRole.PrdAdmin || role === UserRole.PrdUser) {
                rolesArray.push(role);
              }
            });
          }
          this.rolesCtrl.setValue(rolesArray);
          this.rolesCtrl.updateValueAndValidity();
        } else {
          this.form.reset();
          this.rolesCtrl.setValue([]);
          this.airCarriersCtrl.setValue([]);
        }
      });
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  public async update() {
    const result = (await this._employees
      .updatePrdPermissions(this.form.value)
      .toPromise()) as CommandResult;

    if (result.errors.length > 0) {
      this._snackBar.open('Error updating employee PRD permissions.', undefined, {
        duration: 2000,
      });
      this.errors$.next(result.errors);
      this.clicked = false;
    }

    if (result.errors.length === 0 && result.status === CommandResultStatus.Succeeded) {
      this._snackBar.open('Succesfully updated employee PRD permissions.', undefined, {
        duration: 2000,
      });
      this.clicked = false;
    }
  }

  public clear() {
    this.employeeCtrl.reset();
  }
}
